<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import Button from '@components/Button.svelte';
  import Input from '@components/Input.svelte';
  import Modal from '@components/Modal.svelte';
  import Link from '@components/Link.svelte';
  import Dropdown from '@components/Dropdown.svelte';
  import Notification from '@utils/notification';
  import Log from '@utils/log';
  import UserAjax from '@ajax/user';
  import { getCurrentUserStore } from '@stores/current_user';
  import ConvertAffiliateLinkModal from './ConvertAffiliateLinkModal.svelte';

  const dispatch = createEventDispatcher();

  export let showModal = false;
  export let mobile = false;

  let codeEl;
  let user = getCurrentUserStore('default');
  let unsub = user.subscribe((x) => (user = x));
  let countryOptions = [
    'Australia',
    'Brazil',
    'Canada',
    'China',
    'Egypt',
    'France',
    'Germany',
    'India',
    'Italy',
    'Japan',
    'Mexico',
    'Netherlands',
    'Poland',
    'Saudi Arabia',
    'Singapore',
    'Spain',
    'Sweden',
    'Turkey',
    'United Arab Emirates',
    'United Kingdom',
    'United States',
  ];

  let processedCountryOptions = countryOptions.map((country) => {
    return { label: country, value: country };
  });
  let userCountry = user.amazon_affiliate_country;
  let selectedCountry = userCountry || 'United States';
  let selectedCountryOptions = userCountry
    ? { value: userCountry, label: userCountry }
    : { value: 'United States', label: 'United States' };

  let showConvertExistingLinksModal = false;

  const saveAffiliateCode = async function () {
    let data = {
      amazon_affiliate_code: codeEl.value,
      amazon_affiliate_country: selectedCountry,
    };

    await UserAjax.update(user, data);
    user.set({ ...user, ...data });

    if (!mobile) {
      Notification.send({ text: 'Saved amazon associates id' });
    }

    Log.info(
      `[AmazonID ${codeEl.value === '' ? 'Removed' : 'Added'}] user: ${user && user.name} | amazon id: ${codeEl.value}`,
      false,
      '#affiliates'
    );

    if (codeEl.value !== '') {
      moveToConvertExistingLinkModal();
    } else {
      close();
    }
  };

  const moveToConvertExistingLinkModal = function () {
    showConvertExistingLinksModal = true;
  };

  const close = function () {
    showConvertExistingLinksModal = false;
    showModal = false;
    dispatch('close');
  };

  onDestroy(() => {
    unsub();
  });
</script>

<style style="text/scss" lang="scss">
  .ui-container {
    margin-left: 35px;
    margin-right: 35px;

    .ui-img {
      margin-top: 40px;
      text-align: center;
    }

    .ui-header {
      font-weight: bold;
      font-size: 12px;
      line-height: 15px;
      text-align: center;
      text-transform: uppercase;
      color: #1a1a1a;
      margin-top: -50px;
    }

    .ui-description {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #7f7f7f;
      margin-top: 32px;
    }

    .ui-input {
      margin-top: 16px;
      display: flex;
      flex-direction: row;
      align-items: center;

      &:first-of-type {
        margin-top: 40px;
      }
    }

    .ui-save {
      margin-top: 30px;
      text-align: center;
    }

    .ui-label {
      font-size: 15px;
      line-height: 15px;
      opacity: 0.5;
      font-weight: normal;
      display: flex;
      flex-basis: 36%;
    }
  }

  .mobile {
    .ui-input {
      flex-direction: column;
      align-items: normal;
    }

    .ui-label {
      margin-bottom: 10px;
    }
  }

  .mobile .ui-label {
    flex-basis: 100%;
  }

  * :global(.ui-input .input-container) {
    flex-basis: 64%;
  }

  * :global(.mobile .ui-input .input-container) {
    flex-basis: 100%;
  }

  * :global(.ui-input .input-container input) {
    margin: 0 !important;
  }

  * :global(.ui-input .dropdown) {
    flex-basis: 64%;
    border-radius: 15px;
    border: 2px solid #ececec;
  }

  * :global(.ui-input .dropdown .dropdown-button) {
    color: #495057 !important;
    padding-bottom: 6px !important;
    padding-left: 21px !important;
  }

  * :global(.ui-input .dropdown .dropdown-options) {
    height: 150px;
    overflow-y: scroll;
  }
</style>

{#if showConvertExistingLinksModal}
  <ConvertAffiliateLinkModal
    mobile={mobile}
    msg="Would you like to convert existing Amazon links on your Benable lists into affiliate links?"
    successMessage="Updating amazon affiliate links (takes up to 10 minutes)"
    linkRegex="amazon."
    on:close={close} />
{:else}
  <div class="svelte-modal">
    <Modal mobile={mobile} on:close={close} bind:show={showModal} fadeInDuration={200}>
      <div slot="content" class:mobile={mobile}>
        <div class="ui-container">
          <div class="ui-header">Add your Amazon Associates ID</div>
          <div class="ui-img"><img src="/images/payout/amazon.svg" /></div>
          <div class="ui-description">
            Amazon manages its own affiliate program, Amazon Associates. Benable can auto-affiliate any Amazon links
            once you have an Amazon Associates account. Your sales and cashback will show up directly in your Amazon
            Associates account as usual. Enter your Amazon Associates ID below, or easily
            <Link href="https://affiliate-program.amazon.com/" text="apply for an Associates ID here." newTab={true} />
          </div>
          <div class="ui-input-wrapper">
            <div class="ui-input">
              <div class="ui-label">Amazon Associates ID</div>
              <Input
                focus={true}
                value={user.amazon_affiliate_code}
                on:enter={saveAffiliateCode}
                bind:inputEl={codeEl} />
            </div>
            <div class="ui-input">
              <div class="ui-label">Program country</div>
              {#if true}
                <Dropdown
                  showSelectedOption={true}
                  selectedOption={selectedCountryOptions}
                  options={processedCountryOptions}
                  optionsClass={'purple-scrollbar'}
                  on:selected={(e) => {
                    selectedCountry = e.detail.option.value;
                  }} />
              {/if}
            </div>
          </div>
          <div class="ui-save">
            <Button on:click={saveAffiliateCode} text="Save" style="width: 160px; height: 48px;" />
          </div>
        </div>
      </div>
    </Modal>
  </div>
{/if}
